import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Gauge, Sparkles, ArrowUpRight, ArrowDownRight, Check, X, CheckCircle2 } from "lucide-react";
import { sr } from "../../lib/sr";

const money = (v) => `$${Number(v || 0).toLocaleString()}`;
const CONF = { high: ["High confidence", "sr-badge-green"], medium: ["Medium confidence", "sr-badge-amber"], low: ["Estimated", "sr-badge-gray"] };

export default function BudgetRecommendations() {
  const [recs, setRecs] = useState(null);
  const [summary, setSummary] = useState({});
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () =>
    sr.budgetRecs().then((r) => { setRecs(r.recommendations || []); setSummary(r.summary || {}); }).catch(() => setRecs([]));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await sr.generateBudgetRecs();
      toast[r.created ? "success" : "message"](r.created ? `Found ${r.created} budget suggestion(s)` : (r.message || "Nothing to suggest"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not analyze budgets"); }
    finally { setGenerating(false); }
  };

  const act = async (rec, approve) => {
    setBusy(rec.id);
    try {
      if (approve) { const r = await sr.approveBudgetRec(rec.id); toast.success(r.note || "Budget updated"); }
      else { await sr.rejectBudgetRec(rec.id); toast.message("Suggestion dismissed"); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(null); }
  };

  if (!recs) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;
  const pending = recs.filter((r) => r.status === "pending");
  const history = recs.filter((r) => r.status !== "pending").slice(0, 8);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="budget-recs-page">
      <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
        <Gauge size={22} /> Budget Recommendations
      </h1>
      <p className="sr-muted" style={{ marginTop: 6 }}>
        We read each live campaign's recent pacing and results, then suggest a right-sized monthly budget. Nothing changes until you approve — every suggestion is yours to apply or dismiss.
      </p>

      <div className="sr-card" style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }} data-testid="budget-recs-summary">
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
          <div><div className="sr-muted" style={{ fontSize: 12 }}>Pending</div><strong style={{ color: "var(--navy)", fontSize: 20 }}>{summary.pending || 0}</strong></div>
          <div><div className="sr-muted" style={{ fontSize: 12 }}>Suggested increase</div><strong style={{ color: "#0E7A55", fontSize: 20 }}>+{money(summary.monthly_increase)}</strong></div>
          <div><div className="sr-muted" style={{ fontSize: 12 }}>Suggested trim</div><strong style={{ color: "#B42318", fontSize: 20 }}>-{money(summary.monthly_decrease)}</strong></div>
        </div>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={generate} disabled={generating} data-testid="budget-generate">
          {generating ? <Loader2 size={13} className="sr-spin" /> : <><Sparkles size={14} /> Analyze budgets</>}
        </button>
      </div>

      {pending.length === 0 ? (
        <div className="sr-card sr-empty" style={{ marginTop: 14, textAlign: "center", padding: 34 }} data-testid="budget-recs-empty">
          <Gauge size={26} style={{ color: "var(--muted)" }} />
          <p style={{ marginTop: 8 }}>No pending budget suggestions. Tap <strong>Analyze budgets</strong> and we'll review your active campaigns.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          {pending.map((r) => {
            const up = r.direction === "increase";
            const [clabel, ccls] = CONF[r.confidence] || CONF.low;
            return (
              <div key={r.id} className="sr-card" data-testid={`budget-rec-${r.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ color: "var(--navy)", textTransform: "capitalize" }}>{r.campaign_name}</strong>
                  <span className={`sr-badge ${up ? "sr-badge-green" : "sr-badge-amber"}`} data-testid={`budget-dir-${r.id}`}>
                    {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} {up ? "Increase" : "Reduce"} {Math.abs(r.pct_change)}%
                  </span>
                  <span className={`sr-badge ${ccls}`}>{clabel}</span>
                  {r.metrics?.evidence === "modeled" && <span className="sr-badge sr-badge-gray">estimated delivery</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                  <span className="sr-muted" style={{ fontSize: 13 }}>{money(r.current_budget)}/mo</span>
                  <span style={{ color: "var(--muted)" }}>→</span>
                  <strong style={{ color: up ? "#0E7A55" : "#B42318", fontSize: 18 }}>{money(r.recommended_budget)}/mo</strong>
                  {r.metrics && <span className="sr-muted" style={{ fontSize: 11.5 }}>· pacing {r.metrics.pacing_pct}% · CTR {r.metrics.ctr}%</span>}
                </div>
                <p className="sr-muted" style={{ marginTop: 8, marginBottom: 0, fontSize: 13.5 }}>{r.rationale}</p>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy === r.id} onClick={() => act(r, true)} data-testid={`budget-approve-${r.id}`}>
                    {busy === r.id ? <Loader2 size={13} className="sr-spin" /> : <><Check size={14} /> Apply {money(r.recommended_budget)}</>}
                  </button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy === r.id} onClick={() => act(r, false)} data-testid={`budget-reject-${r.id}`}>
                    <X size={14} /> Dismiss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <h2 style={{ color: "var(--navy)", fontSize: 15, margin: "0 0 8px" }}>Recently actioned</h2>
          <div style={{ display: "grid", gap: 6 }}>
            {history.map((r) => (
              <div key={r.id} className="sr-card" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid={`budget-history-${r.id}`}>
                {r.status === "applied" ? <CheckCircle2 size={15} style={{ color: "#0E7A55" }} /> : <X size={15} style={{ color: "var(--muted)" }} />}
                <strong style={{ color: "var(--navy)", fontSize: 13.5, textTransform: "capitalize" }}>{r.campaign_name}</strong>
                <span className="sr-muted" style={{ fontSize: 12.5 }}>
                  {r.status === "applied" ? `Applied ${money(r.applied_budget || r.recommended_budget)}/mo` : "Dismissed"} · was {money(r.current_budget)}/mo
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
